import { lexer, type Token, type Tokens } from 'marked';
import { Predicate } from 'effect';

export type MarkdownSpeechSectionType =
  | 'heading'
  | 'body'
  | 'citation'
  | 'caption'
  | 'footnote'
  | 'endnote'
  | 'table'
  | 'reference-entry';

export type MarkdownSpeechSection = {
  id: string;
  type: MarkdownSpeechSectionType;
  text: string;
  cleanedText: string;
  pageNumber: number;
};

export type MarkdownSpeechDocument = {
  title?: string;
  text: string;
  sections: MarkdownSpeechSection[];
};

type ParsedFrontmatter = { body: string; title?: string | undefined };

export type MarkdownToSpeechDocumentOptions = {
  skipNonNarrative?: boolean;
};

function looksLikeFrontmatter(block: string): boolean {
  const firstLine = block.split('\n').find((line) => line.trim().length > 0);
  return (
    firstLine !== undefined &&
    /^[A-Za-z0-9_.-]+[ \t]*[:=]/.test(firstLine.trim())
  );
}

function parseFrontmatter(markdown: string): ParsedFrontmatter {
  const normalized = markdown.replaceAll('\r\n', '\n').replace(/^\uFEFF/, '');
  const match = /^\s*(---|\+\+\+)\n([\s\S]*?)\n\1(?:\n|$)/.exec(normalized);
  const block = match?.[2];
  if (!match || block === undefined || !looksLikeFrontmatter(block)) {
    return { body: normalized };
  }
  const titleMatch = /^title:[ \t]*(.+?)[ \t]*$/m.exec(block);
  const title = titleMatch?.[1]?.replaceAll(/^["']|["']$/g, '').trim();
  return { body: normalized.slice(match[0].length), title: title || undefined };
}

function stripMdxConstructs(markdown: string): string {
  return markdown
    .replaceAll(
      /^[ \t]*import\s+[^\n]*?\sfrom\s+['"][^'"\n]+['"][ \t]*;?[ \t]*$/gm,
      '',
    )
    .replaceAll(/^[ \t]*import\s+['"][^'"\n]+['"][ \t]*;?[ \t]*$/gm, '')
    .replaceAll(
      /^[ \t]*export\s+(?:default\b|const\b|let\b|var\b|function\b|class\b|async\b|\{|\*)[^\n]*$/gm,
      '',
    )
    .replaceAll(/^[ \t]*\{[^\n{}]*\}[ \t]*$/gm, '');
}

const NAMED_TEXT_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ensp: ' ',
  emsp: ' ',
  thinsp: ' ',
  mdash: '\u2014',
  ndash: '\u2013',
  hellip: '\u2026',
  lsquo: '\u2018',
  rsquo: '\u2019',
  sbquo: '\u201A',
  ldquo: '\u201C',
  rdquo: '\u201D',
  bdquo: '\u201E',
  laquo: '\u00AB',
  raquo: '\u00BB',
  copy: '\u00A9',
  reg: '\u00AE',
  trade: '\u2122',
  deg: '\u00B0',
  times: '\u00D7',
  divide: '\u00F7',
  middot: '\u00B7',
  bull: '\u2022',
  euro: '\u20AC',
  pound: '\u00A3',
  cent: '\u00A2',
  yen: '\u00A5',
};

function decodeTextEntities(text: string): string {
  return text.replaceAll(
    /&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, body: string) => {
      if (body.startsWith('#')) {
        const isHex = body[1] === 'x' || body[1] === 'X';
        const codePoint = isHex
          ? Number.parseInt(body.slice(2), 16)
          : Number.parseInt(body.slice(1), 10);
        if (
          Number.isFinite(codePoint) &&
          codePoint > 0 &&
          codePoint <= 0x10ffff
        ) {
          try {
            return String.fromCodePoint(codePoint);
          } catch {
            return match;
          }
        }
        return match;
      }
      return NAMED_TEXT_ENTITIES[body] ?? match;
    },
  );
}

function stripHtml(text: string) {
  return text
    .replaceAll(/<script\b[\s\S]*?<\/script>/gi, '')
    .replaceAll(/<style\b[\s\S]*?<\/style>/gi, '')
    .replaceAll(/<[^>]+>/g, ' ');
}

function cleanInlineText(text: string) {
  return decodeTextEntities(text).replaceAll(/\s+/g, ' ').trim();
}

function classifyMarkdownHeadingText(text: string): MarkdownSpeechSectionType {
  const trimmed = text.trim();
  if (/^(references|bibliography|works cited|literature cited)$/i.test(trimmed))
    return 'reference-entry';
  if (/^footnotes?$/i.test(trimmed)) return 'footnote';
  if (/^endnotes?$/i.test(trimmed)) return 'endnote';
  return 'heading';
}

function classifyMarkdownBodyText(
  text: string,
  nonNarrativeSectionType: MarkdownSpeechSectionType | undefined,
): MarkdownSpeechSectionType {
  const trimmed = text.trim();
  if (nonNarrativeSectionType) return nonNarrativeSectionType;
  if (/^\[\^[^\]]+\]:/.test(trimmed)) return 'footnote';
  if (/^(figure|fig\.|table)\s+\d+/i.test(trimmed)) return 'caption';
  if (/^(\[\d+\]|\d+\.|\d+\))\s+/.test(trimmed)) return 'reference-entry';
  return 'body';
}

function shouldSkipMarkdownSection(
  type: MarkdownSpeechSectionType,
  options: MarkdownToSpeechDocumentOptions,
) {
  return (
    options.skipNonNarrative === true &&
    (type === 'citation' ||
      type === 'caption' ||
      type === 'footnote' ||
      type === 'endnote' ||
      type === 'table' ||
      type === 'reference-entry')
  );
}

function tokenRawText(token: Token) {
  return 'text' in token && Predicate.isString(token.text) ? token.text : '';
}

function inlineText(tokens: readonly Token[] | undefined): string {
  if (!tokens) return '';

  return cleanInlineText(
    tokens
      .map((token) => {
        switch (token.type) {
          case 'text':
          case 'escape':
          case 'codespan': {
            return tokenRawText(token);
          }
          case 'strong':
          case 'em':
          case 'del':
          case 'link': {
            return inlineText(token.tokens);
          }
          case 'image': {
            return tokenRawText(token);
          }
          case 'br': {
            return '\n';
          }
          case 'html': {
            return stripHtml(tokenRawText(token));
          }
          case 'checkbox': {
            return '';
          }
          default: {
            return 'tokens' in token && Array.isArray(token.tokens)
              ? inlineText(token.tokens)
              : '';
          }
        }
      })
      .filter(Boolean)
      .join(''),
  );
}

function sectionId(prefix: string, index: number) {
  return `${prefix}-${index.toString()}`;
}

function appendSection(
  sections: MarkdownSpeechSection[],
  type: MarkdownSpeechSectionType,
  text: string,
  options: MarkdownToSpeechDocumentOptions = {},
) {
  const cleanedText = cleanInlineText(text);
  if (!cleanedText) return;
  if (shouldSkipMarkdownSection(type, options)) return;

  sections.push({
    id: sectionId(type, sections.length),
    type,
    text: cleanedText,
    cleanedText,
    pageNumber: 1,
  });
}

function orderedListStart(list: Tokens.List) {
  return list.ordered ? (Predicate.isNumber(list.start) ? list.start : 1) : 0;
}

function listItemPrefix(list: Tokens.List, index: number) {
  return list.ordered ? `${(orderedListStart(list) + index).toString()}. ` : '';
}

function appendListItemSections(
  sections: MarkdownSpeechSection[],
  item: Tokens.ListItem,
  prefix: string,
  type: MarkdownSpeechSectionType,
  options: MarkdownToSpeechDocumentOptions,
) {
  appendSection(sections, type, prefix + blockText(item.tokens), options);
}

function tableRowTexts(table: Tokens.Table) {
  const headers = table.header.map((cell) => inlineText(cell.tokens));
  return table.rows.map((row) =>
    row
      .map((cell, index) => {
        const label = headers[index];
        const value = inlineText(cell.tokens);
        return label && value ? `${label}: ${value}` : value;
      })
      .filter(Boolean)
      .join('. '),
  );
}

function appendTableSections(
  sections: MarkdownSpeechSection[],
  table: Tokens.Table,
  options: MarkdownToSpeechDocumentOptions,
) {
  for (const rowText of tableRowTexts(table)) {
    appendSection(sections, 'table', rowText, options);
  }
}

function tokenInlineText(token: Token) {
  return 'tokens' in token && Array.isArray(token.tokens)
    ? inlineText(token.tokens)
    : cleanInlineText(tokenRawText(token));
}

function tableRowsText(table: Tokens.Table) {
  return tableRowTexts(table).join('\n\n');
}

function blockText(tokens: readonly Token[]) {
  const parts: string[] = [];
  for (const token of tokens) {
    switch (token.type) {
      case 'heading':
      case 'paragraph':
      case 'text': {
        parts.push(tokenInlineText(token));
        break;
      }
      case 'blockquote': {
        if ('tokens' in token && Array.isArray(token.tokens)) {
          parts.push(blockText(token.tokens));
        }
        break;
      }
      case 'list': {
        const list = token as Tokens.List;
        parts.push(
          list.items
            .map(
              (item: Tokens.ListItem, index: number) =>
                listItemPrefix(list, index) + blockText(item.tokens ?? []),
            )
            .join('. '),
        );
        break;
      }
      case 'table': {
        parts.push(tableRowsText(token as Tokens.Table));
        break;
      }
      case 'html': {
        parts.push(stripHtml(tokenInlineText(token)));
        break;
      }
      default: {
        break;
      }
    }
  }
  return parts.filter(Boolean).join('\n\n');
}

export function markdownToSpeechDocument(
  markdown: string,
  options: MarkdownToSpeechDocumentOptions = {},
) {
  const { body, title: frontmatterTitle } = parseFrontmatter(markdown);
  const tokens = lexer(stripMdxConstructs(body), { gfm: true });
  const sections: MarkdownSpeechSection[] = [];
  let headingTitle: string | undefined;
  let nonNarrativeSectionType: MarkdownSpeechSectionType | undefined;

  for (const token of tokens) {
    switch (token.type) {
      case 'heading': {
        const heading = token as Tokens.Heading;
        const text = inlineText(heading.tokens);
        if (!headingTitle && heading.depth === 1 && text) headingTitle = text;
        const type = classifyMarkdownHeadingText(text);
        nonNarrativeSectionType = type === 'heading' ? undefined : type;
        appendSection(sections, type, text, options);
        break;
      }
      case 'paragraph':
      case 'text': {
        const text = tokenInlineText(token);
        appendSection(
          sections,
          classifyMarkdownBodyText(text, nonNarrativeSectionType),
          text,
          options,
        );
        break;
      }
      case 'blockquote': {
        if ('tokens' in token && Array.isArray(token.tokens)) {
          const text = blockText(token.tokens);
          appendSection(
            sections,
            classifyMarkdownBodyText(text, nonNarrativeSectionType),
            text,
            options,
          );
        }
        break;
      }
      case 'list': {
        const list = token as Tokens.List;
        list.items.forEach((item, index) =>
          appendListItemSections(
            sections,
            item,
            listItemPrefix(list, index),
            nonNarrativeSectionType ?? 'body',
            options,
          ),
        );
        break;
      }
      case 'table': {
        appendTableSections(sections, token as Tokens.Table, options);
        break;
      }
      case 'html': {
        const text = stripHtml(tokenInlineText(token));
        appendSection(
          sections,
          classifyMarkdownBodyText(text, nonNarrativeSectionType),
          text,
          options,
        );
        break;
      }
      default: {
        break;
      }
    }
  }

  const title = headingTitle ?? frontmatterTitle;
  return {
    ...(title ? { title } : {}),
    text: sections.map((section) => section.cleanedText).join('\n\n'),
    sections,
  } satisfies MarkdownSpeechDocument;
}

export function markdownToPlainText(markdown: string) {
  return markdownToSpeechDocument(markdown).text;
}

export const MARKDOWN_FILE_EXTENSIONS = ['md', 'markdown', 'mdx'] as const;

export const MARKDOWN_MIME_TYPES = [
  'text/markdown',
  'text/x-markdown',
  'text/mdx',
] as const;

export const MARKDOWN_FILE_ACCEPT =
  '.md,.markdown,.mdx,text/markdown,text/x-markdown,text/mdx';

export function isMarkdownFile(file: { name: string; type: string }): boolean {
  const mimeType = file.type.split(';')[0]?.trim().toLowerCase() ?? '';
  if ((MARKDOWN_MIME_TYPES as readonly string[]).includes(mimeType))
    return true;
  const name = file.name.toLowerCase();
  return MARKDOWN_FILE_EXTENSIONS.some((extension) =>
    name.endsWith(`.${extension}`),
  );
}
